// src/components/admin/act/report/ACTDetailedQuestionReport.js
import React, { useEffect, useMemo, useState } from "react";
import { db } from "../../../../firebaseConfig";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { getStorage, ref, getDownloadURL } from "firebase/storage"; // <--- Added Storage imports
import ACTQuestionRow from "./ACTQuestionRow";
import { IoClose } from "react-icons/io5"; 
import { backdropDismiss } from "../../../../utils/backdropDismiss";

const SUBJECT_ORDER = { English: 1, Math: 2, Reading: 3, Science: 4 };

const subjectSort = (a, b) => (SUBJECT_ORDER[a] || 99) - (SUBJECT_ORDER[b] || 99) || String(a).localeCompare(String(b));

const getQNumber = (qid, fallback) => {
  if (typeof fallback === "number" && !Number.isNaN(fallback)) return fallback;
  const m = String(qid || "").match(/_Q(\d+)/i);
  return m ? Number(m[1]) : 0;
};

const splitCorrect = (val) =>
  String(val || "")
    .split(/[,\s/|]+/)
    .map((t) => t.trim().toUpperCase())
    .filter(Boolean);

const styles = {
  container: {
    marginTop: 28,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
  },
  title: { fontSize: 18, fontWeight: 800, margin: "0 0 12px 0", color: "#111827" },
  subjectSection: {
    marginBottom: 22,
    borderRadius: 14,
    border: "1px solid #e5e7eb",
    backgroundColor: "#ffffff",
    boxShadow: "0 10px 30px rgba(15,23,42,0.05)",
    overflow: "hidden",
  },
  header: { padding: "10px 16px", borderBottom: "1px solid #e5e7eb", background: "linear-gradient(90deg, #f9fafb, #eef2ff)" },
  headerTitle: {
    fontSize: 14,
    fontWeight: 900,
    color: "#1f2937",
    margin: 0,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  tableWrapper: { padding: "4px 12px 12px", overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  headerRow: { borderBottom: "1px solid #e5e7eb", textAlign: "left" },
  headerCell: {
    padding: 8,
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: "#6b7280",
    whiteSpace: "nowrap",
  },
  loading: { fontSize: 14, color: "#4b5563" },
  error: {
    color: "#b91c1c",
    backgroundColor: "#fef2f2",
    border: "1px solid #fecaca",
    borderRadius: 10,
    padding: "10px 12px",
    fontSize: 13,
  },
  note: { marginTop: 8, fontSize: 12, color: "#6b7280", lineHeight: 1.4 },

  // Modal Styles
  modalOverlay: {
    position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    zIndex: 9999,
    display: "flex", alignItems: "center", justifyContent: "center",
    backdropFilter: "blur(4px)",
  },
  modalContent: {
    width: "95vw", height: "90vh", maxWidth: 1400,
    backgroundColor: "#fff",
    borderRadius: 16,
    boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
    display: "flex", flexDirection: "column",
    overflow: "hidden",
  },
  modalHeader: {
    padding: "16px 20px", borderBottom: "1px solid #e5e7eb",
    display: "flex", justifyContent: "space-between", alignItems: "center",
    background: "#f9fafb",
  },
  modalTitle: { margin: 0, fontSize: 16, fontWeight: 800, color: "#111827" },
  closeBtn: {
    background: "none", border: "none", cursor: "pointer", fontSize: 24, color: "#6b7280",
    display: "flex", alignItems: "center",
  },
  modalBody: { flex: 1, display: "flex", overflow: "hidden" },
  pane: { flex: 1, overflowY: "auto", padding: 24 },
  paneLeft: { borderRight: "1px solid #e5e7eb", background: "#fdfdfd" },
  paneRight: { background: "#fff" },
  
  // Content rendering
  contentImage: { maxWidth: "100%", height: "auto", display: "block", marginBottom: 16, border: "1px solid #e5e7eb", borderRadius: 8 },
  qText: { fontSize: 16, lineHeight: 1.6, color: "#1f2937", marginBottom: 20 },
  passageText: { fontSize: 16, lineHeight: 1.8, color: "#374151", whiteSpace: "pre-wrap" },
  
  // Answer Key Block (when no options array exists)
  answerBlock: {
    marginTop: 20,
    padding: 16,
    background: "#f9fafb",
    borderRadius: 8,
    border: "1px solid #e5e7eb",
    display: "flex",
    gap: 24,
    alignItems: "center",
  },
  ansLabel: { fontSize: 11, textTransform: "uppercase", color: "#6b7280", fontWeight: 700, marginBottom: 4 },
  ansValue: { fontSize: 18, fontWeight: 900, color: "#111827" },
};

export default function ACTDetailedQuestionReport({ studentId, testId, attemptId }) {
  const [grouped, setGrouped] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  
  // State for the Question Viewer Modal
  const [viewItem, setViewItem] = useState(null);
  const [imageUrls, setImageUrls] = useState({ passage: null, question: null });
  const [imagesLoading, setImagesLoading] = useState(false);

  useEffect(() => {
    if (!studentId || !testId) {
      setGrouped(null);
      setLoading(false);
      return;
    }

    const run = async () => {
      setLoading(true);
      setError("");
      try {
        // 1) Question metadata
        const qSnapshot = await getDocs(collection(db, "act_diagnostic_tests", testId, "questions"));
        const questionMap = new Map();
        qSnapshot.forEach((d) => {
          const data = d.data() || {};
          const key = data.questionId || d.id;
          questionMap.set(key, { ...data, questionId: key });
        });

        // 2) Attempt doc (answers live here)
        const reportRef = doc(db, "diagnostic_results", attemptId || `${studentId}_${testId}`);
        const reportSnap = await getDoc(reportRef);
        if (!reportSnap.exists()) throw new Error("Student report not found.");

        const report = reportSnap.data() || {};
        const answers = report.answers || {};
        const scienceSkipped = !!report.scienceSkipped;

        const temp = {};

        for (const [qid, q] of questionMap.entries()) {
          // If science was skipped, hide science questions entirely
          const subject = q.subject || String(qid).split("_")[0] || "Uncategorized";
          if (scienceSkipped && subject === "Science") continue;

          // Optional: ignore unscored items
          if (String(q.category || "").toLowerCase() === "not scored") continue;

          const studentAnsRaw = answers[qid] ?? "";
          const studentAnswer = String(studentAnsRaw || "").trim().toUpperCase();

          const possible = splitCorrect(q.correctAnswer);
          const isSkipped = studentAnswer === "";
          const isCorrect = !isSkipped && possible.includes(studentAnswer);

          const status = isSkipped ? "Skipped" : isCorrect ? "Correct" : "Incorrect";

          const item = {
            ...q,
            id: qid,
            questionId: qid,
            studentAnswer,
            isCorrect,
            status,
          };

          if (!temp[subject]) temp[subject] = [];
          temp[subject].push(item);
        }

        // Sort subjects and each table by question #
        const sorted = Object.keys(temp)
          .sort(subjectSort)
          .reduce((obj, subject) => {
            obj[subject] = temp[subject].sort(
              (a, b) => getQNumber(a.questionId, a.questionNumber) - getQNumber(b.questionId, b.questionNumber)
            );
            return obj;
          }, {});

        setGrouped(sorted);
      } catch (err) {
        console.error(err);
        setError(err?.message || String(err));
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [studentId, testId, attemptId]);

  // Load Images when viewItem changes
  useEffect(() => {
    setImageUrls({ passage: null, question: null });
    if (!viewItem) return;

    const loadImages = async () => {
      setImagesLoading(true);
      const storage = getStorage();
      const newUrls = { passage: null, question: null };

      try {
        if (viewItem.passageImagePath) {
          const pRef = ref(storage, viewItem.passageImagePath);
          newUrls.passage = await getDownloadURL(pRef);
        }
        if (viewItem.questionImagePath) {
          const qRef = ref(storage, viewItem.questionImagePath);
          newUrls.question = await getDownloadURL(qRef);
        }
      } catch (err) {
        console.error("Error loading images:", err);
      } finally {
        setImageUrls(newUrls);
        setImagesLoading(false);
      }
    };

    loadImages();
  }, [viewItem]);

  const subjects = useMemo(() => Object.keys(grouped || {}), [grouped]);

  if (loading) return <div style={styles.loading}>Loading question breakdown…</div>;
  if (error) return <div style={styles.error}>{error}</div>;
  if (!grouped || subjects.length === 0) return <div style={styles.loading}>No question data.</div>;

  return (
    <div style={styles.container}>
      <div style={styles.title}>Question Breakdown</div>
      <div style={styles.note}>
        Rows are grouped by subject. Click <b>Open</b> to view the passage and question.
      </div>

      {subjects.map((subject) => (
        <div key={subject} style={styles.subjectSection}>
          <div style={styles.header}>
            <p style={styles.headerTitle}>{subject}</p>
          </div>

          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.headerRow}>
                  <th style={styles.headerCell}>Q#</th>
                  <th style={styles.headerCell}>Status</th>
                  <th style={styles.headerCell}>Category</th>
                  <th style={styles.headerCell}>Student&apos;s Answer</th>
                  <th style={styles.headerCell}>Correct Answer</th>
                  <th style={styles.headerCell}>View</th>
                </tr>
              </thead>
              <tbody>
                {grouped[subject].map((item) => (
                  <ACTQuestionRow 
                    key={item.questionId || item.id} 
                    item={item} 
                    onOpen={(itm) => setViewItem(itm)} 
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {/* QUESTION REVIEW MODAL */}
      {viewItem && (
        <div style={styles.modalOverlay} {...backdropDismiss(() => setViewItem(null))}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>
                {viewItem.subject} — Question {getQNumber(viewItem.questionId)}
              </h3>
              <button style={styles.closeBtn} onClick={() => setViewItem(null)}>
                <IoClose />
              </button>
            </div>
            
            <div style={styles.modalBody}>
              {/* LEFT: Passage */}
              <div style={{ ...styles.pane, ...styles.paneLeft }}>
                <h4 style={{marginTop:0, color:'#6b7280', fontSize:12, textTransform:'uppercase'}}>Passage</h4>
                
                {imagesLoading ? (
                  <div style={{color:'#6b7280', fontStyle:'italic'}}>Loading passage image...</div>
                ) : imageUrls.passage ? (
                  <img src={imageUrls.passage} alt="Passage" style={styles.contentImage} />
                ) : viewItem.passageContent ? (
                  <div style={styles.passageText} dangerouslySetInnerHTML={{ __html: viewItem.passageContent }} />
                ) : viewItem.passageText ? (
                  <div style={styles.passageText}>{viewItem.passageText}</div>
                ) : (
                  <div style={{color:'#9ca3af', fontStyle:'italic'}}>No passage content available.</div>
                )}
              </div>

              {/* RIGHT: Question */}
              <div style={{ ...styles.pane, ...styles.paneRight }}>
                 <h4 style={{marginTop:0, color:'#6b7280', fontSize:12, textTransform:'uppercase', marginBottom: 16}}>
                   Question
                 </h4>
                 
                 {imagesLoading ? (
                   <div style={{color:'#6b7280', fontStyle:'italic'}}>Loading question image...</div>
                 ) : imageUrls.question ? (
                   <img src={imageUrls.question} alt="Question" style={styles.contentImage} />
                 ) : (
                   /* Text Fallback */
                   <div style={styles.qText}>
                     {viewItem.questionHtml ? (
                       <div dangerouslySetInnerHTML={{ __html: viewItem.questionHtml }} />
                     ) : (
                       <div>{viewItem.questionText || viewItem.question || "No question text."}</div>
                     )}
                   </div>
                 )}

                 {/* Answer Stats Block */}
                 <div style={styles.answerBlock}>
                   <div>
                     <div style={styles.ansLabel}>Your Answer</div>
                     <div style={{...styles.ansValue, color: viewItem.isCorrect ? '#16a34a' : '#ea580c' }}>
                        {viewItem.studentAnswer || "Skipped"}
                     </div>
                   </div>
                   <div style={{width: 1, height: 40, background:'#e5e7eb'}}></div>
                   <div>
                     <div style={styles.ansLabel}>Correct Answer</div>
                     <div style={styles.ansValue}>{viewItem.correctAnswer}</div>
                   </div>
                   <div style={{width: 1, height: 40, background:'#e5e7eb'}}></div>
                   <div>
                     <div style={styles.ansLabel}>Status</div>
                     <div style={{fontSize: 14, fontWeight: 700, color: '#4b5563', marginTop: 2}}>{viewItem.status}</div>
                   </div>
                 </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}